import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { fetchTicketComments, getProject } from "../../../services/projects";
import { isMeaningfulGigsOrDemo } from "../../../utils/auth";
import { refetchIntervalUnlessMfaStepParked } from "../../../utils/enterpriseMfaStep";
import { deriveReviewInFlightFromTicket } from "../chatUtils";
import { pickCardThumbUrl } from "../components/ProjectStatusPane";
import {
  visibleAiCommentCount,
  visibleHumanCommentCount,
} from "../utils/visibleComments";

export type ChatReviewSummary = {
  id: string;
  ticketId: string;
  name: string;
  versionNumber?: number;
  thumbnailUrl?: string | null;
  aiCommentCount: number;
  humanCommentCount: number;
  isReviewInFlight: boolean;
};

export type UseChatReviewSummariesOptions = {
  /**
   * When true (review in flight), poll ticket + comments so sidebar /
   * thread review cards pick up AI comment counts as they land — not only
   * after the user opens a ticket viewer.
   */
  pollingEnabled?: boolean;
};

/** Query timing for review-card ticket/comment fetches. */
export function chatReviewSummaryQueryTiming(pollingEnabled: boolean): {
  staleTime: number;
  refetchInterval: number | false;
} {
  return {
    staleTime: pollingEnabled ? 0 : 60_000,
    refetchInterval: pollingEnabled ? 5_000 : false,
  };
}

/**
 * Build review-rail card data for standalone chats from per-ticket fetches
 * plus transcript-derived titles.
 */
export function useChatReviewSummaries(
  ticketIds: readonly string[],
  titleMap: ReadonlyMap<string, string>,
  options?: UseChatReviewSummariesOptions,
): { reviews: ChatReviewSummary[]; isLoading: boolean } {
  const isDemoEnterprise = isMeaningfulGigsOrDemo();
  const { staleTime, refetchInterval } = chatReviewSummaryQueryTiming(
    options?.pollingEnabled ?? false,
  );

  // Same stop as `useTicketView`: a parked MFA step makes every tick a re-read
  // of the same step, and each park re-dispatches `openEnterpriseMfaSwitch` —
  // re-opening the TOTP dialog the user is answering, every 5s. The retry
  // predicate in `queryClient` covers the backoff attempts; this covers the
  // poll. Both resume when the step clears.
  const parkedAwareInterval = (query: { state: { error: unknown } }) =>
    refetchIntervalUnlessMfaStepParked(query.state.error, refetchInterval);

  const ticketQueries = useQueries({
    queries: ticketIds.map((ticketId) => ({
      queryKey: ["ticket-view", ticketId],
      queryFn: () => getProject(ticketId),
      staleTime,
      refetchInterval: parkedAwareInterval,
    })),
  });

  const commentQueries = useQueries({
    queries: ticketIds.map((ticketId) => ({
      queryKey: ["ticket-view-comments", ticketId],
      queryFn: () => fetchTicketComments({ ticketId, query: "" }),
      staleTime,
      refetchInterval: parkedAwareInterval,
    })),
  });

  const isLoading =
    ticketQueries.some((q) => q.isLoading) ||
    commentQueries.some((q) => q.isLoading);

  const reviews = useMemo((): ChatReviewSummary[] => {
    return ticketIds.map((ticketId, index) => {
      const ticket = ticketQueries[index]?.data;
      const comments = commentQueries[index]?.data ?? [];
      const boards = ticket?.revisionBoards?.filter((b) => !b.deletedAt) ?? [];
      const latestBoard = boards.at(-1);
      const firstReviewFile = latestBoard?.reviewFiles?.[0] as
        | { source?: string; thumbnailUrl?: string }
        | undefined;
      const thumbnailUrl = pickCardThumbUrl(
        firstReviewFile,
        latestBoard,
        isDemoEnterprise,
      );
      const aiCommentCount = visibleAiCommentCount(comments);
      const humanCommentCount = visibleHumanCommentCount(comments);
      const name =
        titleMap.get(ticketId) ?? ticket?.title ?? `Review ${index + 1}`;
      const versionNumber = boards.length > 0 ? boards.length : undefined;

      return {
        id: ticketId,
        ticketId,
        name,
        versionNumber,
        thumbnailUrl: thumbnailUrl ?? null,
        aiCommentCount,
        humanCommentCount,
        isReviewInFlight: deriveReviewInFlightFromTicket(ticket),
      };
    });
  }, [ticketIds, ticketQueries, commentQueries, titleMap, isDemoEnterprise]);

  return { reviews, isLoading };
}
